import type * as React from 'react';
import { cn } from '@/lib/cn';

// A casca de TODO campo do sistema (WEB-133): célula clara + fio de 1px por dentro
// (`--shadow-inset`), raio 16, 40px no celular e 36px a partir de `sm` (a mesma altura do
// botão `default` e do gatilho do `Select`, pra linha de filtro alinhar). No foco o fundo
// vira o do cartão e o contorno de 2px da regra base de `index.css` cola na borda
// (`outline-offset-0`). Inválido troca o fio pela cor de erro, sem halo.
//
// `fieldShell` é exportada porque o `<select>` nativo e o `Input` do kit usam a MESMA. Não
// traz largura: `Input` e `Textarea` somam `w-full`; o grupo com adorno e o select do kit
// seguem o conteúdo numa linha de filtro (`flex`), como sempre foi.
const fieldShell =
  'min-h-10 min-w-0 rounded-xl bg-secondary/60 text-base shadow-(--shadow-inset) transition-[box-shadow,background-color] duration-150 sm:min-h-9 md:text-sm';

function Input({ className, type, ...props }: React.ComponentProps<'input'>) {
  return (
    <input
      type={type}
      data-slot="input"
      className={cn(
        // `min-h` e não `h`: altura travada em px não acompanha quem aumenta a fonte do
        // navegador e o Chrome corta a base das letras.
        fieldShell,
        'w-full px-3 py-1 selection:bg-primary selection:text-primary-foreground file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50',
        'focus-visible:bg-card focus-visible:outline-offset-0',
        // Somente leitura: sem preenchimento (não convida a digitar), o fio continua.
        '[&[readonly]]:bg-transparent [&[readonly]]:focus-visible:bg-transparent',
        'aria-invalid:shadow-[inset_0_0_0_1px_var(--destructive)]',
        className,
      )}
      {...props}
    />
  );
}

/**
 * Campo com adorno (prefixo "R$", ícone de busca, botão de copiar): a casca é do grupo e o
 * `Input` de dentro fica nu. Uso: `<InputGroup><InputAddon>R$</InputAddon><Input /></InputGroup>`.
 */
function InputGroup({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="input-group"
      className={cn(
        fieldShell,
        'flex items-center gap-2 px-3',
        'focus-within:bg-card has-[[data-slot=input]:focus-visible]:outline-2 has-[[data-slot=input]:focus-visible]:outline-ring',
        'has-[[aria-invalid=true]]:shadow-[inset_0_0_0_1px_var(--destructive)] has-[[data-slot=input]:disabled]:opacity-50',
        // O `Input` de dentro perde a casca própria: quem desenha o campo é o grupo.
        '[&_[data-slot=input]]:min-h-0! [&_[data-slot=input]]:flex-1 [&_[data-slot=input]]:rounded-none [&_[data-slot=input]]:bg-transparent! [&_[data-slot=input]]:px-0 [&_[data-slot=input]]:shadow-none! [&_[data-slot=input]]:outline-none! [&_[data-slot=input]]:disabled:opacity-100',
        className,
      )}
      {...props}
    />
  );
}

function InputAddon({ className, ...props }: React.ComponentProps<'span'>) {
  return (
    <span
      data-slot="input-addon"
      className={cn(
        "flex shrink-0 items-center text-sm text-muted-foreground select-none [&_svg:not([class*='size-'])]:size-4",
        className,
      )}
      {...props}
    />
  );
}

export { fieldShell, Input, InputAddon, InputGroup };
